import React from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  useTheme,
  Tooltip,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  Timer as TimerIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useSubscription } from '../hooks/useSubscription';
import { useNavigate } from 'react-router-dom';

export const TrialIndicator = ({ variant = 'banner' }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isOnTrial, getDaysRemaining, getPlanFeatures } = useSubscription();

  if (!isOnTrial()) return null;

  const daysLeft = getDaysRemaining();
  const isUrgent = daysLeft <= 3;
  const progress = ((14 - daysLeft) / 14) * 100; // Assuming 14-day trial

  const handleUpgradeClick = () => {
    navigate('/settings', { state: { activeTab: 'subscription' } });
  };

  const getTrialMessage = () => {
    if (daysLeft <= 1) return "Trial expires tomorrow!";
    if (daysLeft <= 3) return "Trial ending soon!";
    return `${daysLeft} days left in trial`;
  };

  const features = getPlanFeatures('professional');

  if (variant === 'compact') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          bgcolor: isUrgent ? 'warning.light' : 'info.light',
          borderRadius: 1
        }}
      >
        <TimerIcon fontSize="small" color={isUrgent ? 'warning' : 'info'} />
        <Typography variant="body2" color={isUrgent ? 'warning.dark' : 'info.dark'}>
          {getTrialMessage()}
        </Typography>
        <Button
          size="small"
          variant="contained"
          color={isUrgent ? 'warning' : 'primary'}
          onClick={handleUpgradeClick}
          sx={{ ml: 'auto' }}
        >
          Upgrade Now
        </Button>
      </Box>
    );
  }

  if (variant === 'card') {
    return (
      <Card
        sx={{
          p: 2,
          border: `1px solid ${isUrgent ? theme.palette.warning.main : theme.palette.info.main}`,
          bgcolor: isUrgent ? 'warning.light' : 'info.light',
          position: 'relative',
          overflow: 'visible'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -12,
            left: 16,
            bgcolor: isUrgent ? 'warning.main' : 'info.main',
            color: 'white',
            px: 2,
            py: 0.5,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <StarIcon fontSize="small" />
          <Typography variant="body2" fontWeight="bold">
            Trial Active
          </Typography>
        </Box>

        <Box mt={2}>
          <Typography variant="h6" gutterBottom>
            {getTrialMessage()}
            <Tooltip title="You have access to all premium features during your trial" arrow>
              <IconButton size="small" sx={{ ml: 1 }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              my: 1,
              height: 8,
              borderRadius: 4,
              bgcolor: isUrgent ? 'warning.light' : 'info.light',
              '& .MuiLinearProgress-bar': {
                bgcolor: isUrgent ? 'warning.main' : 'info.main'
              }
            }}
          />

          <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Access to premium features:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                {features.slice(0, 3).map((feature, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      bgcolor: 'background.paper',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    {feature}
                  </Typography>
                ))}
                {features.length > 3 && (
                  <Typography
                    variant="body2"
                    sx={{
                      bgcolor: 'background.paper',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    +{features.length - 3} more
                  </Typography>
                )}
              </Box>
            </Box>
            <Button
              variant="contained"
              color={isUrgent ? 'warning' : 'primary'}
              onClick={handleUpgradeClick}
              sx={{ ml: 2 }}
            >
              Choose Plan
            </Button>
          </Box>
        </Box>
      </Card>
    );
  }

  // Default banner variant
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: isUrgent ? 'warning.light' : 'info.light',
        borderBottom: `1px solid ${isUrgent ? theme.palette.warning.main : theme.palette.info.main}`,
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar - 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2
      }}
    >
      {isUrgent ? (
        <WarningIcon color="warning" />
      ) : (
        <StarIcon color="info" />
      )}
      
      <Typography>
        <strong>{getTrialMessage()}</strong> - You have full access to all premium features
      </Typography>

      <Tooltip title="Lock in your premium features before trial expires" arrow>
        <Button
          variant="contained"
          color={isUrgent ? 'warning' : 'primary'}
          size="small"
          onClick={handleUpgradeClick}
          sx={{ ml: 2 }}
        >
          Choose Your Plan
        </Button>
      </Tooltip>
    </Box>
  );
}; 